import { useState, useEffect, useRef, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '@utils/api';
import { useI18n } from '@/i18n';
import PageHeader from '@components/PageHeader';
import { ApprovalsTabs, StationBadge, EmployerBadge, Toast } from '@components/AbsenceBits';
import { lisbonYear, fmtDays, apiMessage, ENTITLEMENT_DAYS } from '@utils/absences';

const FIELD = 'input min-h-[44px] !text-base md:!text-sm';
// Server refusals that have a friendlier, translated message.
const KNOWN_REFUSALS = {
  'You cannot confirm your own entitlement': 'ent.refusedSelf',
  'Suggestion changed, reload': 'ent.refusedStale',
  'Already confirmed by HR': 'ent.refusedHr',
};

export default function EntitlementConfirm() {
  const { t, lang } = useI18n();
  const { stations, pending } = useOutletContext();
  const thisYear = lisbonYear();
  const [year, setYear] = useState(thisYear);
  const [station, setStation] = useState('');
  const [rows, setRows] = useState(null);          // suggestions
  const [edits, setEdits] = useState({});          // staff_id -> { selected, days, reason }
  const [refusals, setRefusals] = useState({});    // staff_id -> message
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  const load = useCallback(() => {
    const mine = ++seq.current;
    setRows(null);
    const params = { year };
    if (station) params.station = station;
    return api.get('/entitlements/suggestions', { params }).then(({ data }) => {
      if (mine !== seq.current) return;               // a slower, older response must not win
      setRows(data.suggestions || []);
      setEdits({}); setRefusals({}); setError('');
    }).catch(() => {
      if (mine !== seq.current) return;
      setError(t('ent.loadError'));
    });
  }, [year, station, t]);
  useEffect(() => { load(); }, [load]);

  const edit = (id, patch) => setEdits((e) => ({ ...e, [id]: { ...e[id], ...patch } }));
  const view = (r) => ({ selected: false, days: r.suggested_days, reason: '', ...edits[r.staff_id] });
  const changed = (r) => Number(view(r).days) !== Number(r.suggested_days);
  const chosen = (rows || []).filter((r) => view(r).selected);
  const missingReason = chosen.some((r) => changed(r) && !view(r).reason.trim());

  const confirm = async () => {
    if (busy || !chosen.length || missingReason) return;
    setBusy(true);
    try {
      const items = chosen.map((r) => {
        const v = view(r);
        const it = { staff_id: r.staff_id, year, days: Number(v.days), suggestion_id: r.id };
        if (v.reason.trim()) it.reason = v.reason.trim();
        return it;
      });
      const { data } = await api.post('/entitlements/confirm', { items });
      const okIds = new Set((data.items || []).map((i) => i.staff_id));
      const refused = {};
      for (const f of data.refused || []) if (f.staff_id) refused[f.staff_id] = f.reason;
      setRows((list) => list.filter((r) => !okIds.has(r.staff_id)));
      setEdits((e) => Object.fromEntries(Object.entries(e).filter(([id]) => !okIds.has(id))));
      setRefusals(refused);
      const n = data.confirmed || 0;
      const nRef = (data.refused || []).length;
      setToast({ kind: nRef ? 'error' : 'ok', text: nRef ? t('ent.partial', { ok: n, refused: nRef }) : t('ent.confirmedN', { n }) });
    } catch (err) {
      setToast({ kind: 'error', text: apiMessage(err, t('ent.confirmFailed')) });
    } finally { setBusy(false); }
  };

  const refusalText = (msg) => (KNOWN_REFUSALS[msg] ? t(KNOWN_REFUSALS[msg]) : msg);
  const years = [thisYear - 1, thisYear, thisYear + 1];

  return (
    <>
      <PageHeader title={t('appr.title')} subtitle={t('ent.subtitle')} />
      <ApprovalsTabs stations={stations} pending={pending} />
      <div className="px-5 md:px-0 pb-28">
        <Toast toast={toast} />
        {stations?.length === 0 && <div className="card p-6 text-sm text-gray-600">{t('ent.notApprover')}</div>}
        {stations?.length > 0 && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="min-w-0">
                <label className="label" htmlFor="ent-year">{t('ent.year')}</label>
                <select id="ent-year" className={FIELD} value={year} onChange={(e) => setYear(Number(e.target.value))}>
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              {stations.length > 1 && (
                <div className="min-w-0">
                  <label className="label" htmlFor="ent-station">{t('ent.station')}</label>
                  <select id="ent-station" className={FIELD} value={station} onChange={(e) => setStation(e.target.value)}>
                    <option value="">{t('ent.allStations')}</option>
                    {stations.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
            </div>

            {error ? (
              <div role="alert" className="card p-6 text-sm text-red-700">{error}</div>
            ) : rows === null ? (
              <div className="card p-8 text-center text-gray-500">{t('common.loading')}</div>
            ) : rows.length === 0 ? (
              <div className="card p-8 text-center text-gray-600 text-sm" data-testid="ent-empty">{t('ent.empty')}</div>
            ) : (
              <ul className="space-y-3">
                {rows.map((r) => {
                  const v = view(r);
                  const isChanged = changed(r);
                  return (
                    <li key={r.id} className="card p-4" data-testid="ent-row">
                      <div className="flex items-start gap-3">
                        <input type="checkbox" className="mt-1 h-6 w-6 accent-glk-green shrink-0" checked={v.selected}
                               aria-label={t('ent.select', { name: r.staff_name })}
                               onChange={(e) => edit(r.staff_id, { selected: e.target.checked })} />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-base font-semibold text-glk-dark">{r.staff_name}</span>
                            <StationBadge station={r.station} />
                            <EmployerBadge employer={r.employer} />
                          </div>
                          <p className="mt-1 text-sm text-glk-dark">
                            {t('ent.suggested', { n: fmtDays(r.suggested_days, lang) })} · {t('ent.points', { n: fmtDays(r.points, lang) })}
                          </p>
                          <p className="mt-0.5 text-xs text-gray-600">{r.reason}</p>
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-1 gap-2 pl-9">
                        <div>
                          <label className="label" htmlFor={`days-${r.staff_id}`}>{t('ent.days')}</label>
                          <select id={`days-${r.staff_id}`} className={FIELD} value={v.days}
                                  onChange={(e) => edit(r.staff_id, { days: Number(e.target.value) })}>
                            {ENTITLEMENT_DAYS.map((d) => <option key={d} value={d}>{fmtDays(d, lang)}</option>)}
                          </select>
                        </div>
                        {isChanged && (
                          <div>
                            <label className="label" htmlFor={`why-${r.staff_id}`}>{t('ent.reasonRequired')}</label>
                            <input id={`why-${r.staff_id}`} className={FIELD} maxLength={500} value={v.reason}
                                   aria-invalid={v.selected && !v.reason.trim()}
                                   onChange={(e) => edit(r.staff_id, { reason: e.target.value })} />
                          </div>
                        )}
                        {refusals[r.staff_id] && (
                          <p role="alert" className="text-sm text-red-700" data-testid="ent-refused">{refusalText(refusals[r.staff_id])}</p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>

      {stations?.length > 0 && rows?.length > 0 && (
        <div className="fixed bottom-[64px] md:bottom-0 inset-x-0 z-20 bg-white border-t border-gray-200 px-5 py-3 md:static md:border-0 md:bg-transparent md:px-0">
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            <button type="button" className="btn-ghost min-h-[44px]" onClick={load} disabled={busy}>{t('ent.reload')}</button>
            <button type="button" className="btn-primary flex-1 min-h-[44px]" disabled={busy || !chosen.length || missingReason} onClick={confirm}>
              {busy ? t('abs.sending') : t('ent.confirmSelected', { n: chosen.length })}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
